import { Component, computed, inject, input, output, signal } from '@angular/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { CheckBoxState, KENDO_CHECKBOX, KENDO_RADIOBUTTON } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_GRIDLAYOUT, KENDO_PANELBAR, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { KENDO_LISTVIEW, ListViewDataResult, PageChangeEvent } from '@progress/kendo-angular-listview';
import { AgreementDetailedDto } from '@unity/clients';
import { SidebarLayoutComponent } from '@unity/components/layouts/sidebar-layout/sidebar-layout.component';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { MainTileDirective } from '@unity/directives/main-tile.directive';
import { SidebarTileDirective } from '@unity/directives/sidebar-tile.directive';
import { AgreementListSwitches } from '@unity/pages/list-agreement-page/list-agreement-page.component';
import { SortPipe } from '@unity/pipes/sort.pipe';
import { FilterItemToggleEvent, FilterMasterStateEvent, SwitchStateEvent } from '@unity/services/gridify.service';
import { IconService } from '@unity/services/icon.service';
import { LayoutService } from '@unity/services/layout.service';
import { SessionService } from '@unity/services/session.service';
import { CardAgreementSharedComponent } from '@unity/shared/card-agreement-shared/card-agreement-shared.component';
import { FilterState, SWITCHES_KEY } from '@unity/utils/filter-state';

type _KEYS = keyof AgreementDetailedDto;

@Component({
  selector: 'unity-list-agreement-shared',
  standalone: true,
  imports: [
    KENDO_BUTTON,
    KENDO_STACKLAYOUT,
    KENDO_GRIDLAYOUT,
    KENDO_PANELBAR,
    KENDO_LABEL,
    KENDO_CHECKBOX,
    KENDO_RADIOBUTTON,
    SortPipe,
    AuthorizeDirective,
    CardAgreementSharedComponent,
    SidebarLayoutComponent,
    MainTileDirective,
    SidebarTileDirective,
    KENDO_LISTVIEW,
  ],
  templateUrl: './list-agreement-shared.component.html',
  styleUrl: './list-agreement-shared.component.scss',
})
export class ListAgreementSharedComponent {
  private readonly iconService = inject(IconService);
  private readonly sessionService = inject(SessionService);
  private readonly layoutService = inject(LayoutService);

  /* Data */
  protected smallestPageSize = 5;
  protected clearIcon = this.iconService.xCircleIcon;
  protected listItemGap = this.layoutService.listItemGap;
  protected listItemAlign = this.layoutService.listItemAlign;
  protected sidebarWidth = 'max(250px,15vw)';

  /* Inputs */
  public readonly $showAgencyName = input.required<boolean>({ alias: 'showAgencyName' });
  public readonly $agreements = input.required<AgreementDetailedDto[]>({ alias: 'agreements' });
  public readonly $count = input.required<number>({ alias: 'count' });
  public readonly $total = input.required<number>({ alias: 'total' });
  public readonly $isLoading = input.required<boolean>({ alias: 'isLoading' });
  public readonly $valueStates = input.required<FilterState<AgreementDetailedDto>[]>({ alias: 'valueStates' });
  public readonly $switchStates = input.required<SwitchStateEvent<AgreementDetailedDto>[]>({ alias: 'switchStates' });

  /* Signals */
  protected readonly $skip = signal<number>(0);
  protected readonly $pageSize = signal<number>(this.smallestPageSize);

  protected readonly $returned = computed(() => this.$agreements().length);
  protected readonly $pageable = computed(() => this.$total() > this.smallestPageSize);
  protected readonly $containerClass = computed(() => (this.$agreements().length > 0 ? 'tile-container' : undefined));
  protected readonly $filteredDataView = computed<ListViewDataResult>(() => {
    const total = this.$count();
    const agreements = this.$agreements();
    const data = agreements.length === 0 ? [{ name: 'null' }] : agreements;
    return {
      data,
      total,
    };
  });
  protected readonly $assignedMode = computed(
    () => this.$switchStates().find(x => x.item === AgreementListSwitches.isAssigned.item)?.state
  );
  protected readonly $overdueMode = computed(() => this.$switchStates().find(x => x.item === AgreementListSwitches.isOverdue.item)?.state);

  protected readonly $showAgreementToggles = this.sessionService.$isAdministratorUserRole;

  /* Outputs */
  public pageChangeEvent = output<PageChangeEvent>();
  public switchStateEvent = output<SwitchStateEvent<AgreementDetailedDto>>();
  public filterMasterEvent = output<FilterMasterStateEvent<AgreementDetailedDto>>();
  public filterItemToggleEvent = output<FilterItemToggleEvent<AgreementDetailedDto>>();
  public filterClearEvent = output();

  /* Methods */
  protected getShowAllState(all: boolean, none: boolean): CheckBoxState {
    if (all === true) {
      return true;
    }
    if (none === true) {
      return false;
    }
    return 'indeterminate';
  }

  protected setSwitchState(key: _KEYS, state: boolean) {
    this.switchStateEvent.emit({ item: key, state });
  }

  // type hacks in these two handlers because FilterState is overloaded..
  protected onFilterMasterCheck(filterKey: _KEYS | typeof SWITCHES_KEY, state: CheckBoxState) {
    this.filterMasterEvent.emit({ key: filterKey as _KEYS, state });
  }

  protected onFilterItemCheck(filterKey: _KEYS | typeof SWITCHES_KEY, itemKey: string) {
    this.filterItemToggleEvent.emit({ key: filterKey as _KEYS, item: itemKey });
  }

  protected clearAll() {
    this.filterClearEvent.emit();
  }

  protected handlePageChange(event: PageChangeEvent) {
    this.$skip.set(event.skip);
    this.$pageSize.set(event.take);
    this.pageChangeEvent.emit(event);
  }
}
